import { Badge, Box, useTheme } from '@mui/material';
import {
  PickersDay,
  type PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import type { DateTime } from 'luxon';
import { type ReactElement, useState } from 'react';
import useStyles from './DatePicker.styles';

/**
 * Values are Luxon `DateTime`s rather than `Date`s, and that is the whole reason this file changed.
 *
 * A `Date` is an instant with no zone attached, so a component handed one has to guess which
 * calendar to read it against and can only guess the host machine's. "Is this the same day as that"
 * then has a different answer at a desk in Detroit and on the same laptop in Madrid. A `DateTime`
 * arrives carrying its zone, so this component compares what it was given and needs to know nothing
 * about who is reading — which is the only arrangement a presentational package can honestly offer.
 */
interface DatePickerProps {
  value: DateTime | null;
  onChange: (date: DateTime | null) => void;
  minDate: DateTime;
  maxDate: DateTime;
  datesWithData: DateTime[];
}

/**
 * Component that wraps MUI's StaticDatePicker with trip-specific functionality
 * Shows dots under dates that have trip data
 */
const DatePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  datesWithData,
}: DatePickerProps) => {
  const theme = useTheme();
  const { classes } = useStyles();
  // The latest selectable month when nothing is selected yet, rather than "now": the caller has
  // already decided which calendar its bounds are in, and reading a clock here would not know.
  const [currentMonth, setCurrentMonth] = useState<DateTime>(value ?? maxDate);

  const renderDay = (
    pickersDayProps: PickersDayProps<DateTime>
  ): ReactElement => {
    const day = pickersDayProps.day;
    const hasData = datesWithData.some((withData) =>
      withData.hasSame(day, 'day')
    );
    const isSelected = pickersDayProps.selected;
    const key = day.toISODate() ?? String(day.toMillis());

    if (hasData) {
      return (
        <Badge
          key={key}
          overlap="circular"
          variant="dot"
          className={`${classes.dateBadge} ${isSelected ? classes.dateBadgeSelected : ''}`}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <PickersDay {...pickersDayProps} day={day} />
        </Badge>
      );
    }

    return <PickersDay {...pickersDayProps} day={day} key={key} />;
  };

  const shouldDisablePrevNavigation = () =>
    currentMonth.startOf('month') <= minDate.startOf('month');

  const shouldDisableNextNavigation = () =>
    currentMonth.endOf('month') >= maxDate.endOf('month');

  const handleMonthChange = (date: DateTime) => {
    setCurrentMonth(date);
  };

  return (
    <Box className={classes.calendarContainer}>
      <StaticDatePicker<DateTime>
        value={value}
        onChange={onChange}
        onMonthChange={handleMonthChange}
        minDate={minDate}
        maxDate={maxDate}
        slots={{
          day: renderDay,
        }}
        slotProps={{
          actionBar: {
            actions: ['today'],
            className: classes.actionBar,
          },
          previousIconButton: {
            disabled: shouldDisablePrevNavigation(),
            sx: {
              '& *': {
                fill: shouldDisablePrevNavigation()
                  ? theme.new.color.text.tertiary
                  : theme.new.color.text.primary,
              },
            },
          },
          nextIconButton: {
            disabled: shouldDisableNextNavigation(),
            sx: {
              '& *': {
                fill: shouldDisableNextNavigation()
                  ? theme.new.color.text.tertiary
                  : theme.new.color.text.primary,
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default DatePicker;
